import {
  Box,
  Flex,
  Popover,
  PopoverBody,
  PopoverContent,
  PopoverFooter,
  PopoverTrigger,
  Text,
} from '@chakra-ui/react';
import PropTypes from 'prop-types';
import * as React from 'react';
import { useClickAway } from 'react-use';
import Button from '~/components/Button';

import { DayPickerRangeControllerWrapper } from './DayPickerRangeControllerWrapper';
import { DATE_FORMAT, END_DATE, START_DATE } from './constants';

const propTypes = {
  startDate: PropTypes.any,
  endDate: PropTypes.any,
  onDatesChange: PropTypes.func.isRequired,
  fromPlaceholder: PropTypes.string.isRequired,
  toPlaceholder: PropTypes.string.isRequired,
  controllerProps: PropTypes.object,
  onClear: PropTypes.func.isRequired,
  onApply: PropTypes.func.isRequired,
  onDateChange: PropTypes.func.isRequired,
  focusedInput: PropTypes.string,
  onFocusedInputChange: PropTypes.func.isRequired,
  hasDateSet: PropTypes.bool.isRequired,
  isOpen: PropTypes.bool.isRequired,
  onOpen: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  mainButton: PropTypes.node.isRequired,
};

/* eslint-disable react/prop-types */
const SelectionButtons = ({ isFocused, ...props }) => (
  <Button
    border="1px solid black"
    borderRadius="20px"
    backgroundColor="white"
    fontSize="md"
    textTransform="none"
    fontWeight="normal"
    flexGrow="0"
    textAlign="left"
    justifyContent="flex-start"
    letterSpacing="0"
    borderColor={isFocused ? 'primary.500' : 'gray.200'}
    _hover={{ backgroundColor: 'white' }}
    _active={{ backgroundColor: 'white' }}
    width="48%"
    {...props}
  />
);
/* eslint-enable react/prop-types */

const PreText = (props) => <Text color="gray.500" marginRight={2} {...props} />;

export const DesktopDatePicker = ({
  startDate,
  endDate,
  fromPlaceholder,
  toPlaceholder,
  controllerProps,
  onClear,
  onApply,
  onDateChange,
  focusedInput,
  onFocusedInputChange,
  hasDateSet,
  isOpen,
  onClose,
  mainButton,
}) => {
  const popoverRef = React.useRef(null);
  useClickAway(popoverRef, () => {
    onClose();
  });

  return (
    <Popover
      placement="bottom-start"
      // closeOnBlur doesn't work because clicking on the DatePicker is seen as
      // clicking outside the popover.
      // Because of that the useClickAway hook is used instead.
      closeOnBlur={false}
      isOpen={isOpen}
      onClose={onClose}
      isLazy
    >
      <PopoverTrigger>{mainButton}</PopoverTrigger>
      <PopoverContent
        aria-label="Select Date:"
        backgroundColor="white"
        border="none"
        borderRadius="10px"
        boxShadow="floating"
        maxWidth="auto"
        minHeight={{ base: 300, md: 450 }}
        marginTop="5px"
        width="629px"
        zIndex="tooltip"
      >
        <Box ref={popoverRef}>
          <PopoverBody paddingTop={5} paddingBottom="0" paddingX={0}>
            <Box>
              <Flex paddingX={5} justifyContent="space-between">
                <SelectionButtons
                  color={startDate ? 'secondary.500' : 'gray.500'}
                  onClick={() => onFocusedInputChange(START_DATE)}
                  aria-label="Select from date"
                  isFocused={focusedInput === START_DATE}
                >
                  <>
                    <PreText>From</PreText>
                    {startDate
                      ? startDate.format(DATE_FORMAT)
                      : fromPlaceholder}
                  </>
                </SelectionButtons>
                <SelectionButtons
                  color={endDate ? 'secondary.500' : 'gray.500'}
                  onClick={() => onFocusedInputChange(END_DATE)}
                  aria-label="Select to date"
                  isFocused={focusedInput === END_DATE}
                >
                  <>
                    <PreText>To</PreText>
                    {endDate ? endDate.format(DATE_FORMAT) : toPlaceholder}
                  </>
                </SelectionButtons>
              </Flex>
              <DayPickerRangeControllerWrapper
                startDate={startDate}
                endDate={endDate}
                onDatesChange={onDateChange}
                focusedInput={focusedInput}
                onFocusChange={onFocusedInputChange}
                numberOfMonths={2}
                {...controllerProps}
              />
            </Box>
          </PopoverBody>
          <PopoverFooter border="0" paddingTop={0} paddingX={5} paddingY={5}>
            <Box height="1px" backgroundColor="gray.400" marginBottom={3} />
            <Flex
              alignItems="center"
              flexDirection="row-reverse"
              justifyContent="space-between"
            >
              <Flex justifyContent="flex-end">
                <Button onClick={onClose} variant="ghost" marginRight={2}>
                  Cancel
                </Button>
                <Button
                  onClick={() => {
                    onApply();
                    onClose();
                  }}
                  colorScheme="secondary"
                >
                  Apply
                </Button>
              </Flex>
              {hasDateSet && (
                <Button
                  onClick={() => {
                    onClear();
                    onClose();
                  }}
                  variant="ghost"
                >
                  Clear Selection
                </Button>
              )}
            </Flex>
          </PopoverFooter>
        </Box>
      </PopoverContent>
    </Popover>
  );
};

DesktopDatePicker.propTypes = propTypes;
